"use client";

import React, { useMemo, useState } from "react";
import { BookOpen, Search } from "lucide-react";
import { Input, Card, CardContent, EmptyState } from "@wtc/ui";
import { useGetMspDocuments } from "@/lib/hooks/api/msp-docs/msp-docs-graphql";
import { MspDocumentCard } from "@/features/msp-docs/components/MspDocumentCard";
import type { CompanyTabComponentProps } from "@/lib/types/company";

// ============================================================================
// COMPANY CLIENT DOCUMENTS TAB (customer-safe)
// ============================================================================
//
// The client-facing "Documents" tab on the company detail modal/page. Unlike
// the internal `CompanyDocumentation` tab (which lets employees author/edit MSP
// docs) and the `Drive` tab (which exposes internal Google Drive contents),
// this tab is `clientVisible` and is meant to surface ONLY customer-safe MSP
// documentation for the company a client_user is permitted to see.
//
// Data access is governed by Supabase RLS on the MSP docs source — this
// component never exposes internal Drive contents.
//
// TODO(customer-safe-docs): there is not yet a dedicated "customer-safe" flag
// on MSP documents. Until one exists, this tab renders the company's MSP docs
// read-only (no create/edit affordances) so a client can read what WTC has
// published for them. When a customer-visibility flag is added to MSP docs,
// add it to `graphqlFilters` here so only explicitly customer-safe docs list.

export const CompanyClientDocuments: React.FC<CompanyTabComponentProps> = ({ companyId }) => {
  const [searchTerm, setSearchTerm] = useState("");

  // pg_graphql filter shape: each field is an operator object (eq / ilike).
  // RLS further restricts the result set to what the viewer is permitted to read.
  const graphqlFilters = useMemo(() => {
    const filters: Record<string, unknown> = {
      is_active: { eq: true },
    };
    if (companyId) filters.company_id = { eq: companyId };
    if (searchTerm.trim()) filters.title = { ilike: `%${searchTerm.trim()}%` };
    return filters;
  }, [companyId, searchTerm]);

  const { data, isLoading } = useGetMspDocuments({
    first: 20,
    filter: graphqlFilters,
    skip: !companyId,
  });

  const documents = useMemo(
    () => data?.msp_documentsCollection?.edges?.map((e) => e.node) ?? [],
    [data],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <BookOpen className="h-5 w-5 text-blue-600" />
        <h3 className="text-sm font-semibold text-gray-900">Documents</h3>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search documents…"
          className="pl-9"
        />
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="py-10">
            <p className="text-center text-sm text-gray-500">Loading documents…</p>
          </CardContent>
        </Card>
      ) : documents.length === 0 ? (
        <EmptyState
          icon={BookOpen}
          title="No documents yet"
          description="There are no documents published for your account yet. Your WTC team will share guides and references here as they become available."
        />
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {documents.map((doc) => (
            // Read-only surface: MspDocumentCard renders a link to the doc and
            // exposes no edit/delete affordances — clients can read, not author.
            <MspDocumentCard key={doc.id} doc={doc} />
          ))}
        </div>
      )}
    </div>
  );
};
